// Fondo animado de la página pública. Uno por estilo (plan §2b-bis), portado del catálogo de fondos de Cuórum
// con los arreglos de la revisión del 17-oct: base plana, sin desenfoques, capas HTML con transform, pausa
// fuera de pantalla y respeto de prefers-reduced-motion (este último en CSS).
import { useEffect, useRef, useState } from 'react';
import type { BackgroundKind } from './tokens.ts';

export function EventBackground({ kind, motion = true }: { kind: BackgroundKind; motion?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setVisible(entry?.isIntersecting ?? true), { threshold: 0 });
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className="ev-bg" aria-hidden="true" data-kind={kind} data-motion={motion ? 'on' : 'off'} data-paused={visible ? 'false' : 'true'}>
      {kind === 'dots' && (
        <div className="ev-bg-layer ev-bg-dots ev-bg-fade"><span className="ev-bg-anim" /></div>
      )}
      {kind === 'paper' && (
        <>
          <div className="ev-bg-layer ev-bg-paper ev-bg-fade" />
          <svg className="ev-bg-layer ev-bg-paper-ring" viewBox="0 0 400 400">
            <circle cx="200" cy="200" r="170" fill="none" style={{ stroke: 'var(--ev-bg-rule-strong)' }} strokeWidth="1.5" />
            <circle cx="200" cy="200" r="120" fill="none" style={{ stroke: 'var(--ev-bg-rule-strong)' }} strokeWidth="1" strokeDasharray="3 7" />
          </svg>
        </>
      )}
      {kind === 'aurora' && (
        <>
          <div className="ev-bg-layer ev-bg-glow ev-bg-anim" data-n="a" />
          <div className="ev-bg-layer ev-bg-glow ev-bg-anim" data-n="b" />
          <svg className="ev-bg-layer ev-bg-stars" viewBox="0 0 1200 600" preserveAspectRatio="xMidYMin slice">
            <g style={{ fill: 'var(--ev-bg-star)' }}>
              {STARS.map(([cx, cy, r]) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />)}
            </g>
          </svg>
        </>
      )}
      {kind === 'topo' && (
        <svg className="ev-bg-layer ev-bg-topo ev-bg-anim" viewBox="0 0 1200 700" preserveAspectRatio="xMidYMin slice">
          <g fill="none" style={{ stroke: 'var(--ev-bg-rule)' }} strokeWidth="1.4">
            {CONTOURS.map((d) => <path key={d} d={d} />)}
          </g>
        </svg>
      )}
      {kind === 'ribbons' && (
        <>
          <div className="ev-bg-layer ev-bg-ribbon ev-bg-anim" data-n="1" />
          <div className="ev-bg-layer ev-bg-ribbon ev-bg-anim" data-n="2" />
          <div className="ev-bg-layer ev-bg-ribbon ev-bg-anim" data-n="3" />
        </>
      )}
    </div>
  );
}

const STARS: ReadonlyArray<readonly [number, number, number]> = [
  [140, 90, 1.6], [380, 60, 1.2], [610, 140, 1.8], [860, 70, 1.3], [1060, 190, 1.6], [250, 260, 1.1], [980, 330, 1.2], [720, 300, 1],
];

const CONTOURS = [
  'M-120 520 C80 330 230 400 360 265 S620 60 780 185 1030 430 1320 185',
  'M-100 580 C95 390 250 460 395 320 S640 115 805 240 1050 485 1300 250',
  'M-70 640 C120 450 285 520 430 385 S670 175 840 300 1075 545 1280 320',
  'M110 80 C250 -20 410 10 500 110 S690 290 850 170 1080 -10 1250 100',
  'M175 125 C295 40 415 65 510 165 S705 340 865 220 1080 45 1210 155',
];
